(() => {
  "use strict";

  /* ============================================================
   * Onlydream — product card swatches + custom quick add
   * Aurora 5.0.3
   *
   * ONE interaction strategy for every viewport: a single
   * capture-phase "click" listener on window. No pointerdown /
   * pointerup / touchend / submit handlers competing with it.
   * ============================================================ */

  const MOBILE_QUERY = window.matchMedia("(max-width: 768px)");

  const SWATCH_SELECTOR =
    "[data-color-swatches-item]:not([data-color-swatches-hidden-count])";

  /*
   * product-card.liquid renders the {{ buttons }} capture TWICE inside
   * every <product-card>: once in .product-card__actions (on image) and
   * once in .product-card__details-inner (under image). Aurora hides one
   * of them with CSS depending on the button-position settings.
   *
   * The trigger is therefore ALWAYS resolved from event.composedPath(),
   * never from card.querySelector(), so we can only ever get the copy
   * the user actually touched.
   */
  const QUICK_ADD_SELECTOR = [
    "button[data-product-card-quick-view-button]",
    "button[data-product-card-button]",
    "product-card .product-card__btn"
  ].join(",");

  /* Things inside a product card we must never treat as a cart click. */
  const IGNORE_SELECTOR = [
    "[data-color-swatches-hidden-count]",
    "[data-carousel-prev]",
    "[data-carousel-next]",
    "[data-carousel-button]",
    "[data-product-media-tabs-item]",
    ".product-card__dynamic-dots"
  ].join(",");

  const PRODUCT_CACHE = new Map();
  const DESKTOP_RESTORE_TIMERS = new WeakMap();

  let sheet = null;
  let resetTimer = null;
  let lastOpenAt = 0;

  const state = {
    card: null,
    trigger: null,
    product: null,
    colorIndex: -1,
    optionIndices: [],
    selections: new Map(),
    adding: false,
    opening: false,
    scrollY: 0
  };

  console.info("[Onlydream product cards] Loaded v10");

  /* ------------------------------------------------------------
   * General utilities
   * ------------------------------------------------------------ */

  function normalize(value) {
    return String(value || "").trim().toLocaleLowerCase();
  }

  function unique(values) {
    return [
      ...new Set(
        values.filter(
          (value) =>
            value !== undefined && value !== null && value !== ""
        )
      )
    ];
  }

  function getVariantOptions(variant) {
    if (Array.isArray(variant?.options)) {
      return variant.options.map(String);
    }

    return [variant?.option1, variant?.option2, variant?.option3]
      .filter((value) => value !== undefined && value !== null)
      .map(String);
  }

  function getOptionName(option, index = 0) {
    if (typeof option === "string") {
      return option;
    }

    return (
      option?.name ||
      option?.label ||
      option?.title ||
      `Opzione ${index + 1}`
    );
  }

  function getProductOptionCount(product) {
    const productOptionsCount = Array.isArray(product?.options)
      ? product.options.length
      : 0;

    const variantOptionsCount =
      product?.variants?.reduce(
        (maximum, variant) =>
          Math.max(maximum, getVariantOptions(variant).length),
        0
      ) || 0;

    return Math.max(productOptionsCount, variantOptionsCount);
  }

  function isDefaultOption(option, index = 0) {
    return [
      "title",
      "default title",
      "titolo",
      "titolo predefinito"
    ].includes(normalize(getOptionName(option, index)));
  }

  function findVariantById(product, variantId) {
    if (!product || !variantId) {
      return null;
    }

    return (
      product.variants.find(
        (variant) => String(variant.id) === String(variantId)
      ) || null
    );
  }

  function getFirstAvailableVariant(product) {
    return (
      product?.variants?.find((variant) => variant.available) ||
      product?.variants?.[0] ||
      null
    );
  }

  /* ------------------------------------------------------------
   * Product JSON
   * ------------------------------------------------------------ */

  function getProductJsonUrl(card) {
    const rawUrl =
      card?.dataset?.url ||
      card?.querySelector(".product-card__title")?.getAttribute("href") ||
      "";

    if (!rawUrl) {
      return "";
    }

    try {
      const url = new URL(rawUrl, window.location.origin);

      url.hash = "";
      url.search = "";
      url.pathname = url.pathname.replace(/\/$/, "") + ".js";

      return url.toString();
    } catch (error) {
      return "";
    }
  }

  async function fetchProduct(card) {
    const url = getProductJsonUrl(card);

    if (!url) {
      throw new Error("Product URL not found.");
    }

    if (PRODUCT_CACHE.has(url)) {
      return PRODUCT_CACHE.get(url);
    }

    const response = await fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });

    if (!response.ok) {
      throw new Error("The product could not be loaded.");
    }

    const product = await response.json();

    PRODUCT_CACHE.set(url, product);

    return product;
  }

  /* ------------------------------------------------------------
   * Variant image swapping
   * ------------------------------------------------------------ */

  function getVariantImageElement(card, variantId) {
    return (
      [...card.querySelectorAll("img[data-variant-id]")].find(
        (image) =>
          String(image.dataset.variantId) === String(variantId)
      ) || null
    );
  }

  function extractImageData(image) {
    if (!image) {
      return null;
    }

    const src =
      image.getAttribute("data-src") ||
      image.getAttribute("src") ||
      image.currentSrc ||
      "";

    const srcset =
      image.getAttribute("data-srcset") ||
      image.getAttribute("srcset") ||
      "";

    const sizes =
      image.getAttribute("data-sizes") ||
      image.getAttribute("sizes") ||
      "";

    if (!src && !srcset) {
      return null;
    }

    return {
      src,
      srcset,
      sizes,
      alt: image.getAttribute("alt") || "",
      aspectRatio: image.getAttribute("data-aspectratio") || "",
      objectPosition: image.style.objectPosition || ""
    };
  }

  function addImageWidth(imageUrl, width = 900) {
    if (!imageUrl) {
      return "";
    }

    try {
      const url = new URL(imageUrl, window.location.origin);

      url.searchParams.set("width", String(width));

      return url.toString();
    } catch (error) {
      return imageUrl;
    }
  }

  async function getVariantImageData(card, variantId) {
    const sourceImage = getVariantImageElement(card, variantId);
    const renderedImageData = extractImageData(sourceImage);

    if (
      renderedImageData &&
      (renderedImageData.srcset ||
        (renderedImageData.src &&
          !renderedImageData.src.startsWith("data:")))
    ) {
      return renderedImageData;
    }

    const product = await fetchProduct(card);
    const variant = findVariantById(product, variantId);

    if (!variant) {
      return null;
    }

    const featuredImage =
      variant.featured_image?.src ||
      variant.featured_image ||
      variant.featured_media?.preview_image?.src ||
      "";

    if (!featuredImage) {
      return null;
    }

    return {
      src: addImageWidth(featuredImage, 900),
      srcset: "",
      sizes: "",
      alt: `${product.title || ""} ${variant.title || ""}`.trim(),
      aspectRatio: "",
      objectPosition: ""
    };
  }

  function applyImageData(target, imageData) {
    if (!target || !imageData) {
      return false;
    }

    target.classList.remove("lazyload", "lazyloading");
    target.classList.add(
      "lazyloaded",
      "ls-is-cached",
      "product-card__img--visible"
    );

    target.removeAttribute("data-src");
    target.removeAttribute("data-srcset");
    target.removeAttribute("data-sizes");

    if (imageData.srcset) {
      target.setAttribute("srcset", imageData.srcset);
    } else {
      target.removeAttribute("srcset");
    }

    target.setAttribute("sizes", imageData.sizes || "100vw");

    if (imageData.src) {
      target.setAttribute("src", imageData.src);
    }

    if (imageData.alt) {
      target.setAttribute("alt", imageData.alt);
    }

    if (imageData.aspectRatio) {
      target.setAttribute("data-aspectratio", imageData.aspectRatio);
    }

    if (imageData.objectPosition) {
      target.style.objectPosition = imageData.objectPosition;
    }

    target.style.opacity = "1";

    return true;
  }

  function getMobileCarousel(card) {
    const carousel = card.querySelector(
      "carousel-component.product-card__carousel"
    );

    if (!carousel) {
      return null;
    }

    const styles = window.getComputedStyle(carousel);

    if (styles.display === "none" || carousel.offsetWidth === 0) {
      return null;
    }

    return carousel;
  }

  function createCarouselImage(firstSlide) {
    if (!firstSlide) {
      return null;
    }

    const image = document.createElement("img");

    image.className =
      "product-card__img product-card__img--visible shape__target-image";

    image.setAttribute("data-main-media", "");
    image.setAttribute("data-product-card-featured-image", "");
    image.loading = "eager";
    image.style.zIndex = "2";

    firstSlide.appendChild(image);

    return image;
  }

  function getMobilePreviewImage(card) {
    const carousel = getMobileCarousel(card);

    if (carousel) {
      const firstSlide = carousel.querySelector(".product-card__slide");

      if (!firstSlide) {
        return { image: null, carousel };
      }

      const existingImage = firstSlide.querySelector("img");

      return {
        image: existingImage || createCarouselImage(firstSlide),
        carousel
      };
    }

    const visibleImage = [
      ...card.querySelectorAll("img.product-card__img")
    ].find((image) => image.offsetWidth > 0 && image.offsetHeight > 0);

    return {
      image: visibleImage || card.querySelector("img[data-main-media]"),
      carousel: null
    };
  }

  function getDesktopImagesContainer(card) {
    return (
      card.querySelector(".product-card__images") ||
      card.querySelector(".product-card__inner") ||
      card
    );
  }

  function showDesktopVariantImage(card, variantId, imageData) {
    const container = getDesktopImagesContainer(card);

    if (!container) {
      return false;
    }

    const images = [...container.querySelectorAll("img.product-card__img")];

    let selectedImage =
      images.find(
        (image) =>
          String(image.dataset.variantId) === String(variantId)
      ) || null;

    if (!selectedImage) {
      selectedImage =
        images.find((image) => image.hasAttribute("data-main-media")) ||
        images[0] ||
        null;
    }

    if (!selectedImage) {
      return false;
    }

    if (
      String(selectedImage.dataset.variantId || "") !== String(variantId)
    ) {
      applyImageData(selectedImage, imageData);
    }

    images.forEach((image) => {
      const selected = image === selectedImage;

      image.classList.toggle("product-card__img--visible", selected);
      image.style.opacity = selected ? "1" : "0";
      image.style.zIndex = selected ? "3" : "1";
    });

    card.classList.add("od-product-card-has-selection");

    return true;
  }

  function restoreDesktopSelection(card, delay = 180) {
    if (MOBILE_QUERY.matches || !card) {
      return;
    }

    const existingTimer = DESKTOP_RESTORE_TIMERS.get(card);

    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    const timer = window.setTimeout(async () => {
      const variantId = card.dataset.odSelectedVariantId || "";

      if (!variantId) {
        return;
      }

      try {
        const imageData = await getVariantImageData(card, variantId);

        if (imageData) {
          showDesktopVariantImage(card, variantId, imageData);
        }
      } catch (error) {
        console.error("[Onlydream swatches]", error);
      }
    }, delay);

    DESKTOP_RESTORE_TIMERS.set(card, timer);
  }

  /* ------------------------------------------------------------
   * Swatch selection
   * ------------------------------------------------------------ */

  function updateProductLinks(card, variantId) {
    card
      .querySelectorAll(
        [
          ".product-card__link",
          ".product-card__title",
          ".product-card__carousel-container"
        ].join(",")
      )
      .forEach((link) => {
        const href = link.getAttribute("href");

        if (!href) {
          return;
        }

        try {
          const url = new URL(href, window.location.origin);

          url.searchParams.set("variant", variantId);

          link.setAttribute(
            "href",
            url.pathname + url.search + url.hash
          );
        } catch (error) {
          /* Ignore malformed links. */
        }
      });
  }

  function setSelectedSwatch(swatchesComponent, selectedSwatch) {
    swatchesComponent
      .querySelectorAll(SWATCH_SELECTOR)
      .forEach((swatch) => {
        const selected = swatch === selectedSwatch;

        swatch.classList.toggle("od-swatch-selected", selected);
        swatch.classList.toggle("selected", selected);
        swatch.setAttribute("aria-pressed", String(selected));
      });
  }

  function updateSwatchLabel(swatchesComponent, color) {
    const label = swatchesComponent.querySelector(
      "[data-color-swatches-label]"
    );

    if (label) {
      label.textContent = color || "";
    }
  }

  async function activateSwatch(swatch, card) {
    const swatchesComponent = swatch.closest("color-swatches");

    if (!card || !swatchesComponent) {
      return;
    }

    const variantId = swatch.dataset.variantId || "";
    const color = swatch.dataset.color || "";

    if (!variantId) {
      return;
    }

    card.dataset.odSelectedVariantId = variantId;
    card.dataset.odSelectedColor = color;

    setSelectedSwatch(swatchesComponent, swatch);
    updateSwatchLabel(swatchesComponent, color);
    updateProductLinks(card, variantId);

    try {
      const imageData = await getVariantImageData(card, variantId);

      if (!imageData) {
        throw new Error("No variant image found.");
      }

      if (MOBILE_QUERY.matches) {
        const { image: previewImage, carousel } =
          getMobilePreviewImage(card);

        if (!previewImage) {
          throw new Error("No mobile preview image found.");
        }

        applyImageData(previewImage, imageData);

        if (
          carousel?.embla &&
          typeof carousel.embla.scrollTo === "function"
        ) {
          carousel.embla.scrollTo(0, true);
        }
      } else {
        const changed = showDesktopVariantImage(
          card,
          variantId,
          imageData
        );

        if (!changed) {
          throw new Error("No desktop preview image found.");
        }

        restoreDesktopSelection(card, 220);
      }
    } catch (error) {
      console.error("[Onlydream swatches]", error);
    }
  }

  /* ------------------------------------------------------------
   * Bottom sheet / modal
   * ------------------------------------------------------------ */

  function ensureSupportStyles() {
    if (document.getElementById("OdProductCardV5Styles")) {
      return;
    }

    const style = document.createElement("style");

    style.id = "OdProductCardV5Styles";

    style.textContent = `
      .od-quick-add__color-preview {
        display: block;
        flex: 0 0 auto;
        padding-top: 14px;
      }

      .od-quick-add__color-preview[hidden] {
        display: none !important;
      }

      .od-quick-add__color-name {
        display: block;
        margin-bottom: 9px;
        font-size: 13px;
        line-height: 1.3;
        font-weight: 600;
        letter-spacing: 0.01em;
      }

      .od-quick-add__gallery {
        display: flex;
        gap: 8px;
        padding-bottom: 2px;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior-x: contain;
      }

      .od-quick-add__gallery::-webkit-scrollbar {
        display: none;
      }

      .od-quick-add__thumb {
        flex: 0 0 auto;
        width: 54px;
        height: 72px;
        object-fit: cover;
        background: rgb(0 0 0 / 5%);
        border-radius: 8px;
        user-select: none;
        -webkit-user-drag: none;
      }

      .od-quick-add__color-visual {
        display: flex;
        flex: 0 0 36px;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
      }

      .od-quick-add__color-visual .color-swatch,
      .od-quick-add__color-visual .image-swatches__item-body-wrapper {
        position: relative !important;
        left: auto !important;
        display: block !important;
        width: 36px !important;
        min-width: 36px !important;
        height: 36px !important;
        margin: 0 !important;
        overflow: hidden;
        box-shadow: 0 0 0 1px rgb(0 0 0 / 14%);
      }

      .od-quick-add__color-name {
        min-width: 0;
        font-size: 14px;
        line-height: 1.35;
        font-weight: 500;
      }

      product-card [data-color-swatches-item] .od-swatch-selected,
      product-card [data-color-swatches-item].od-swatch-selected .color-swatch,
      product-card [data-color-swatches-item].od-swatch-selected .image-swatches__item-body-wrapper {
        outline: 2px solid currentColor;
        outline-offset: 3px;
      }

      @media screen and (min-width: 769px) {
        body.od-quick-add-open {
          overflow: hidden;
        }

        .od-quick-add {
          position: fixed;
          inset: 0;
          z-index: 10001;
          visibility: hidden;
          pointer-events: none;
        }

        .od-quick-add.is-open {
          visibility: visible;
          pointer-events: auto;
        }

        .od-quick-add__overlay {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          padding: 0;
          background: rgb(0 0 0 / 38%);
          border: 0;
          opacity: 0;
          backdrop-filter: blur(2px);
          transition: opacity 220ms ease;
        }

        .od-quick-add.is-open .od-quick-add__overlay {
          opacity: 1;
        }

        .od-quick-add__sheet {
          position: absolute;
          top: 50%;
          left: 50%;
          display: flex;
          flex-direction: column;
          width: min(460px, calc(100vw - 48px));
          max-height: min(720px, calc(100vh - 64px));
          padding: 0 28px 28px;
          color: var(--gsc-text-color-100, #171717);
          background: var(--gsc-background-color-100, #fff);
          border: 1px solid rgb(0 0 0 / 7%);
          border-radius: 24px;
          box-shadow: 0 28px 90px rgb(0 0 0 / 24%);
          overflow: hidden;
          opacity: 0;
          transform: translate(-50%, -46%) scale(0.96);
          transition:
            opacity 220ms ease,
            transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .od-quick-add.is-open .od-quick-add__sheet {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }

        .od-quick-add__handle {
          display: none;
        }

        .od-quick-add__header {
          display: flex;
          align-items: flex-start;
          gap: 18px;
          padding: 26px 0 21px;
          border-bottom: 1px solid rgb(0 0 0 / 8%);
        }

        .od-quick-add__heading {
          flex: 1;
          min-width: 0;
        }

        .od-quick-add__title {
          margin: 0;
          overflow: hidden;
          font-size: 20px;
          line-height: 1.3;
          font-weight: 650;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .od-quick-add__close {
          display: flex;
          flex: 0 0 auto;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          padding: 0;
          color: inherit;
          background: transparent;
          border: 1px solid rgb(0 0 0 / 10%);
          border-radius: 50%;
          cursor: pointer;
        }

        .od-quick-add__close svg {
          width: 20px;
          height: 20px;
        }

        .od-quick-add__loading {
          display: none;
          align-items: center;
          justify-content: center;
          min-height: 180px;
          gap: 12px;
        }

        .od-quick-add.is-loading .od-quick-add__loading {
          display: flex;
        }

        .od-quick-add.is-loading .od-quick-add__content {
          display: none;
        }

        .od-quick-add__content {
          padding-top: 24px;
          overflow-x: hidden;
          overflow-y: auto;
        }

        .od-quick-add__options {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .od-quick-add__values {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
        }

        .od-quick-add__value {
          min-height: 50px;
          padding: 10px;
          color: inherit;
          background: transparent;
          border: 1px solid rgb(0 0 0 / 15%);
          border-radius: 11px;
          cursor: pointer;
        }

        .od-quick-add__value.is-selected {
          color: var(--gsc-background-color-100, #fff);
          background: var(--gsc-text-color-100, #171717);
          border-color: var(--gsc-text-color-100, #171717);
        }
      }
    `;

    document.head.appendChild(style);
  }

  function ensureSheet() {
    ensureSupportStyles();

    if (sheet) {
      return sheet;
    }

    sheet = document.createElement("div");
    sheet.className = "od-quick-add";
    sheet.setAttribute("aria-hidden", "true");

    sheet.innerHTML = `
      <button
        class="od-quick-add__overlay"
        type="button"
        data-od-sheet-close
        tabindex="-1"
        aria-label="Chiudi"
      ></button>

      <section
        class="od-quick-add__sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="OdQuickAddTitle"
      >
        <div class="od-quick-add__handle"></div>

        <header class="od-quick-add__header">
          <div class="od-quick-add__heading">
            <h2
              class="od-quick-add__title"
              id="OdQuickAddTitle"
              data-od-sheet-title
            ></h2>
          </div>

          <button
            class="od-quick-add__close"
            type="button"
            data-od-sheet-close
            aria-label="Chiudi"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              ></path>
            </svg>
          </button>
        </header>

        <div
          class="od-quick-add__color-preview"
          data-od-sheet-color-preview
          aria-label="Colore selezionato"
          hidden
        >
          <span
            class="od-quick-add__color-name"
            data-od-sheet-color-name
          ></span>

          <div
            class="od-quick-add__gallery"
            data-od-sheet-gallery
          ></div>
        </div>

        <div class="od-quick-add__loading" data-od-sheet-loading>
          <span class="od-quick-add__spinner" aria-hidden="true"></span>
          <span data-od-sheet-loading-text>Caricamento...</span>
        </div>

        <div class="od-quick-add__content" data-od-sheet-content>
          <div class="od-quick-add__options" data-od-sheet-options></div>

          <p
            class="od-quick-add__error"
            role="alert"
            data-od-sheet-error
          ></p>

          <button
            class="od-quick-add__submit"
            type="button"
            data-od-sheet-submit
            hidden
          >
            Aggiungi al carrello
          </button>
        </div>
      </section>
    `;

    document.body.appendChild(sheet);

    sheet.addEventListener("click", handleSheetClick);

    return sheet;
  }

  function lockPage() {
    state.scrollY = window.scrollY || 0;

    document.body.style.top = `-${state.scrollY}px`;
    document.body.classList.add("od-quick-add-open");
  }

  function unlockPage() {
    document.body.classList.remove("od-quick-add-open");
    document.body.style.top = "";

    window.scrollTo(0, state.scrollY || 0);
  }

  function openSheet() {
    const currentSheet = ensureSheet();

    clearTimeout(resetTimer);

    currentSheet.setAttribute("aria-hidden", "false");

    lockPage();

    requestAnimationFrame(() => {
      currentSheet.classList.add("is-open");
    });
  }

  function resetState() {
    state.card = null;
    state.trigger = null;
    state.product = null;
    state.colorIndex = -1;
    state.optionIndices = [];
    state.selections = new Map();
    state.adding = false;
    state.opening = false;
  }

  function closeSheet() {
    if (!sheet) {
      return;
    }

    sheet.classList.remove("is-open");
    sheet.setAttribute("aria-hidden", "true");

    unlockPage();

    resetTimer = window.setTimeout(() => {
      clearSheetError();
      resetState();
    }, 300);
  }

  function setTriggerLoading(trigger, loading) {
    if (!trigger) {
      return;
    }

    trigger.classList.toggle("loading", loading);
    trigger.toggleAttribute("disabled", loading);
    trigger.setAttribute("aria-busy", String(loading));
  }

  function setSheetLoading(loading, text = "Caricamento...") {
    const currentSheet = ensureSheet();

    currentSheet.classList.toggle("is-loading", loading);

    const textElement = currentSheet.querySelector(
      "[data-od-sheet-loading-text]"
    );

    if (textElement) {
      textElement.textContent = text;
    }
  }

  function clearSheetError() {
    const errorElement = ensureSheet().querySelector(
      "[data-od-sheet-error]"
    );

    if (errorElement) {
      errorElement.textContent = "";
      errorElement.classList.remove("is-visible");
    }
  }

  function showSheetError(message) {
    const errorElement = ensureSheet().querySelector(
      "[data-od-sheet-error]"
    );

    if (!errorElement) {
      return;
    }

    errorElement.textContent =
      message || "Non è stato possibile aggiungere il prodotto.";

    errorElement.classList.add("is-visible");
  }

  function showGlobalError(message) {
    const bodyElement = document.querySelector("body-element");

    if (
      bodyElement &&
      typeof bodyElement.showNotification === "function"
    ) {
      bodyElement.showNotification(message, "warning", 6000);
    }
  }

  /* ------------------------------------------------------------
   * Variant resolution
   * ------------------------------------------------------------ */

  function findColorOptionIndex(
    product,
    selectedVariant,
    selectedColor,
    card
  ) {
    const optionCount = getProductOptionCount(product);

    const colorNames = [
      "color",
      "colour",
      "colore",
      "couleur",
      "farbe",
      "cor"
    ];

    for (let index = 0; index < optionCount; index += 1) {
      const normalizedName = normalize(
        getOptionName(product.options?.[index], index)
      );

      if (
        colorNames.some(
          (colorName) =>
            normalizedName === colorName ||
            normalizedName.includes(colorName)
        )
      ) {
        return index;
      }
    }

    if (selectedVariant && selectedColor) {
      const matchedIndex = getVariantOptions(selectedVariant).findIndex(
        (value) => normalize(value) === normalize(selectedColor)
      );

      if (matchedIndex >= 0) {
        return matchedIndex;
      }
    }

    /*
     * Custom swatch labels can differ from the real Shopify option
     * values. Fall back to the option that actually varies between the
     * variants the swatches point at.
     */
    if (card) {
      const swatchVariants = [...card.querySelectorAll(SWATCH_SELECTOR)]
        .map((swatch) =>
          findVariantById(product, swatch.dataset.variantId)
        )
        .filter(Boolean);

      for (let index = 0; index < optionCount; index += 1) {
        const values = new Set(
          swatchVariants.map((variant) =>
            normalize(getVariantOptions(variant)[index])
          )
        );

        values.delete("");

        if (values.size > 1) {
          return index;
        }
      }
    }

    return -1;
  }

  function variantMatchesSelections(variant, ignoredIndex = null) {
    const options = getVariantOptions(variant);

    for (const [index, value] of state.selections.entries()) {
      if (index === ignoredIndex || !value) {
        continue;
      }

      if (normalize(options[index]) !== normalize(value)) {
        return false;
      }
    }

    return true;
  }

  function optionValueIsAvailable(optionIndex, value) {
    if (!state.product) {
      return false;
    }

    return state.product.variants.some((variant) => {
      if (!variant.available) {
        return false;
      }

      const options = getVariantOptions(variant);

      if (normalize(options[optionIndex]) !== normalize(value)) {
        return false;
      }

      return variantMatchesSelections(variant, optionIndex);
    });
  }

  function getOptionValues(optionIndex) {
    return unique(
      state.product.variants.map(
        (variant) => getVariantOptions(variant)[optionIndex]
      )
    );
  }

  function clearInvalidSelections(changedIndex) {
    state.optionIndices.forEach((optionIndex) => {
      if (optionIndex === changedIndex) {
        return;
      }

      const selectedValue = state.selections.get(optionIndex);

      if (
        selectedValue &&
        !optionValueIsAvailable(optionIndex, selectedValue)
      ) {
        state.selections.delete(optionIndex);
      }
    });
  }

  function allOptionsSelected() {
    return state.optionIndices.every((optionIndex) =>
      Boolean(state.selections.get(optionIndex))
    );
  }

  function findSelectedVariant() {
    if (!state.product) {
      return null;
    }

    return (
      state.product.variants.find(
        (variant) =>
          variant.available && variantMatchesSelections(variant)
      ) || null
    );
  }

  /* ------------------------------------------------------------
   * Per-colour gallery (position grouping)
   *
   * Shopify only links ONE image to a variant (featured_image), so a
   * per-colour gallery has to be inferred. Each colour's featured image
   * is treated as an anchor, and every media item from that anchor up to
   * the next colour's anchor is taken to belong to that colour. This
   * assumes photos are uploaded grouped by colour, which is the normal
   * ordering. If the grouping yields nothing, we fall back to the single
   * variant image so the strip is never empty.
   * ------------------------------------------------------------ */

  const GALLERY_LIMIT = 10;

  function getVariantMediaPosition(variant) {
    const position =
      variant?.featured_media?.position ??
      variant?.featured_image?.position;

    return Number.isFinite(position) ? position : null;
  }

  function buildColorAnchors(product, colorIndex) {
    const anchors = new Map();

    product.variants.forEach((variant) => {
      const color = getVariantOptions(variant)[colorIndex];

      if (!color) {
        return;
      }

      const position = getVariantMediaPosition(variant);

      if (!Number.isFinite(position)) {
        return;
      }

      const key = normalize(color);
      const existing = anchors.get(key);

      if (existing === undefined || position < existing) {
        anchors.set(key, position);
      }
    });

    return anchors;
  }

  function getProductImageMedia(product) {
    return (product?.media || [])
      .filter((item) => item && item.media_type === "image")
      .map((item) => ({
        position: item.position,
        src: item.preview_image?.src || item.src || "",
        alt: item.alt || ""
      }))
      .filter((item) => item.src && Number.isFinite(item.position))
      .sort((a, b) => a.position - b.position);
  }

  function getVariantFallbackImage(variant) {
    const src =
      variant?.featured_image?.src ||
      variant?.featured_media?.preview_image?.src ||
      "";

    return src ? [{ src, alt: variant?.title || "" }] : [];
  }

  function getColorGallery(product, colorIndex, colorValue, variant) {
    if (!product) {
      return [];
    }

    const images = getProductImageMedia(product);

    if (colorIndex < 0 || !colorValue || images.length === 0) {
      return getVariantFallbackImage(variant);
    }

    const anchors = buildColorAnchors(product, colorIndex);
    const start = anchors.get(normalize(colorValue));

    if (!Number.isFinite(start)) {
      return getVariantFallbackImage(variant);
    }

    const nextAnchor = [...anchors.values()]
      .sort((a, b) => a - b)
      .find((position) => position > start);

    const end = Number.isFinite(nextAnchor) ? nextAnchor : Infinity;

    const group = images.filter(
      (image) => image.position >= start && image.position < end
    );

    return group.length
      ? group.slice(0, GALLERY_LIMIT)
      : getVariantFallbackImage(variant);
  }

  /* ------------------------------------------------------------
   * Sheet rendering
   * ------------------------------------------------------------ */

  function updateSheetHeader() {
    const currentSheet = ensureSheet();

    const title = currentSheet.querySelector("[data-od-sheet-title]");
    const preview = currentSheet.querySelector(
      "[data-od-sheet-color-preview]"
    );
    const gallery = currentSheet.querySelector("[data-od-sheet-gallery]");
    const colorName = currentSheet.querySelector(
      "[data-od-sheet-color-name]"
    );

    if (title) {
      title.textContent =
        state.product?.title ||
        state.card
          ?.querySelector(".product-card__title")
          ?.textContent?.trim() ||
        "";
    }

    if (!preview || !gallery || !colorName) {
      return;
    }

    gallery.replaceChildren();

    const selectedVariantId =
      state.card?.dataset?.odSelectedVariantId || "";

    const selectedVariant = findVariantById(
      state.product,
      selectedVariantId
    );

    const selectedVariantOptions = getVariantOptions(selectedVariant);

    /*
     * The exact Shopify option value wins over the swatch display label.
     */
    const selectedColor =
      state.colorIndex >= 0
        ? selectedVariantOptions[state.colorIndex] ||
          state.selections.get(state.colorIndex) ||
          state.card?.dataset?.odSelectedColor ||
          ""
        : state.card?.dataset?.odSelectedColor || "";

    const swatches = state.card
      ? [...state.card.querySelectorAll(SWATCH_SELECTOR)]
      : [];

    const selectedSwatch =
      swatches.find(
        (swatch) =>
          String(swatch.dataset.variantId) === String(selectedVariantId)
      ) ||
      swatches.find(
        (swatch) =>
          normalize(swatch.dataset.color) === normalize(selectedColor)
      ) ||
      swatches.find((swatch) =>
        swatch.classList.contains("od-swatch-selected")
      ) ||
      swatches[0];

    const shots = getColorGallery(
      state.product,
      state.colorIndex,
      selectedColor,
      selectedVariant || getFirstAvailableVariant(state.product)
    );

    if (shots.length) {
      shots.forEach((shot) => {
        const thumb = document.createElement("img");

        thumb.className = "od-quick-add__thumb";
        thumb.src = addImageWidth(shot.src, 220);
        thumb.alt = shot.alt || selectedColor || "";
        thumb.loading = "lazy";
        thumb.draggable = false;

        gallery.appendChild(thumb);
      });
    } else {
      /* No usable photo: fall back to the swatch itself. */
      const visual = selectedSwatch?.querySelector(
        [".color-swatch", ".image-swatches__item-body-wrapper"].join(",")
      );

      if (visual) {
        const wrapper = document.createElement("span");

        wrapper.className = "od-quick-add__color-visual";
        wrapper.appendChild(visual.cloneNode(true));

        gallery.appendChild(wrapper);
      }
    }

    colorName.textContent = selectedColor || "";

    preview.hidden = !gallery.childElementCount && !selectedColor;

    preview.setAttribute(
      "aria-label",
      selectedColor ? `Colore ${selectedColor}` : "Colore selezionato"
    );
  }

  function renderOptions() {
    const currentSheet = ensureSheet();

    const optionsContainer = currentSheet.querySelector(
      "[data-od-sheet-options]"
    );

    const submitButton = currentSheet.querySelector(
      "[data-od-sheet-submit]"
    );

    if (!optionsContainer || !submitButton) {
      return;
    }

    optionsContainer.replaceChildren();

    state.optionIndices.forEach((optionIndex) => {
      const rawOption = state.product.options?.[optionIndex];

      let optionName = getOptionName(rawOption, optionIndex);

      const normalizedOptionName = normalize(optionName);

      if (
        normalizedOptionName === "size" ||
        normalizedOptionName === "sizes"
      ) {
        optionName = "Taglia";
      }

      const group = document.createElement("div");

      group.className = "od-quick-add__option";

      const label = document.createElement("div");

      label.className = "od-quick-add__option-label";
      label.textContent = optionName;

      const valuesWrapper = document.createElement("div");

      valuesWrapper.className = "od-quick-add__values";

      getOptionValues(optionIndex).forEach((value) => {
        const available = optionValueIsAvailable(optionIndex, value);

        const selected =
          normalize(state.selections.get(optionIndex)) ===
          normalize(value);

        const button = document.createElement("button");

        button.type = "button";
        button.className = "od-quick-add__value";
        button.textContent = value;
        button.dataset.odOptionIndex = String(optionIndex);
        button.dataset.odOptionValue = value;
        button.disabled = !available || state.adding;

        button.classList.toggle("is-selected", selected);
        button.setAttribute("aria-pressed", String(selected));

        valuesWrapper.appendChild(button);
      });

      group.appendChild(label);
      group.appendChild(valuesWrapper);

      optionsContainer.appendChild(group);
    });

    const hasMultipleOptionGroups = state.optionIndices.length > 1;

    submitButton.hidden = !hasMultipleOptionGroups;
    submitButton.disabled = !allOptionsSelected() || state.adding;
  }

  async function openQuickAdd(card, trigger) {
    const now =
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();

    if (state.adding || state.opening) {
      return;
    }

    /* Belt and braces against any duplicated event reaching us. */
    if (now - lastOpenAt < 350) {
      return;
    }

    lastOpenAt = now;
    state.opening = true;

    state.card = card;
    state.trigger = trigger;
    state.selections = new Map();

    clearSheetError();
    setTriggerLoading(trigger, true);

    openSheet();
    setSheetLoading(true, "Caricamento...");

    try {
      const product = await fetchProduct(card);

      state.product = product;

      const selectedVariantId = card.dataset.odSelectedVariantId || "";
      const selectedColor = card.dataset.odSelectedColor || "";

      const selectedVariant =
        findVariantById(product, selectedVariantId) ||
        getFirstAvailableVariant(product);

      state.colorIndex = findColorOptionIndex(
        product,
        selectedVariant,
        selectedColor,
        card
      );

      if (state.colorIndex >= 0 && selectedVariant) {
        const exactColorValue = getVariantOptions(selectedVariant)[
          state.colorIndex
        ];

        if (exactColorValue) {
          state.selections.set(state.colorIndex, exactColorValue);
        }
      }

      const optionCount = getProductOptionCount(product);

      state.optionIndices = Array.from(
        { length: optionCount },
        (_, index) => index
      ).filter(
        (index) =>
          index !== state.colorIndex &&
          !isDefaultOption(product.options?.[index], index)
      );

      updateSheetHeader();

      /* Colour-only or single-variant product: add straight away. */
      if (state.optionIndices.length === 0) {
        const directVariant = findSelectedVariant() || selectedVariant;

        if (!directVariant || !directVariant.available) {
          throw new Error("Questa variante non è disponibile.");
        }

        state.opening = false;

        await addVariantToCart(directVariant);

        return;
      }

      setTriggerLoading(trigger, false);
      setSheetLoading(false);
      renderOptions();
    } catch (error) {
      console.error("[Onlydream quick add]", error);

      setTriggerLoading(trigger, false);
      setSheetLoading(false);

      showSheetError(
        error.message ||
          "Non è stato possibile caricare il prodotto."
      );
    } finally {
      state.opening = false;
    }
  }

  /* ------------------------------------------------------------
   * Add to cart through Aurora's own drawer
   * ------------------------------------------------------------ */

  function formatCartError(parsedState) {
    const description =
      parsedState?.description ||
      parsedState?.message ||
      "Non è stato possibile aggiungere il prodotto.";

    if (typeof description === "object") {
      return Object.entries(description)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");
    }

    return String(description);
  }

  async function addVariantToCart(variant) {
    if (!variant || state.adding) {
      return;
    }

    state.adding = true;

    clearSheetError();
    setTriggerLoading(state.trigger, true);
    setSheetLoading(true, "Aggiungo al carrello...");

    try {
      const cartDrawer = document.querySelector("#CartDrawer");
      const cartSectionId = cartDrawer?.dataset?.sectionId || "";

      const formData = new FormData();

      formData.append("id", String(variant.id));
      formData.append("quantity", "1");

      if (cartSectionId) {
        formData.append("sections", cartSectionId);
        formData.append("sections_url", window.location.pathname);
      }

      const root = window.Shopify?.routes?.root || "/";
      const route =
        window.routes?.cart_add_url || `${root}cart/add.js`;

      const response = await fetch(route, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData
      });

      const responseText = await response.text();

      let parsedState;

      try {
        parsedState = JSON.parse(responseText);
      } catch (error) {
        throw new Error("The cart returned an invalid response.");
      }

      if (!response.ok || parsedState.description) {
        throw new Error(formatCartError(parsedState));
      }

      setTriggerLoading(state.trigger, false);

      closeSheet();

      if (
        cartDrawer &&
        cartSectionId &&
        parsedState.sections?.[cartSectionId]
      ) {
        const cartHTML = new DOMParser().parseFromString(
          parsedState.sections[cartSectionId],
          "text/html"
        );

        cartDrawer.updateAsideProductsByParsedState?.(parsedState);
        cartDrawer.updateEmptyStatus?.();

        if (typeof cartDrawer.purchaseHandler === "function") {
          await cartDrawer.purchaseHandler(cartHTML, parsedState);
        }

        console.info(
          "[Onlydream quick add] Added to Aurora cart drawer.",
          { variantId: variant.id, title: variant.title }
        );

        return;
      }

      /* Fallback when the store is not using Aurora's cart drawer. */
      document
        .querySelector(
          '[data-sidebar-button][data-sidebar-id="CartDrawer"]'
        )
        ?.click();
    } catch (error) {
      console.error("[Onlydream quick add]", error);

      state.adding = false;

      setTriggerLoading(state.trigger, false);
      setSheetLoading(false);

      showSheetError(
        error.message ||
          "Non è stato possibile aggiungere il prodotto."
      );

      showGlobalError(
        error.message ||
          "Non è stato possibile aggiungere il prodotto."
      );

      renderOptions();
    }
  }

  /* ------------------------------------------------------------
   * Sheet interactions
   * ------------------------------------------------------------ */

  function handleOptionSelection(button) {
    if (state.adding) {
      return;
    }

    clearSheetError();

    const optionIndex = Number(button.dataset.odOptionIndex);
    const optionValue = button.dataset.odOptionValue || "";

    if (!Number.isInteger(optionIndex) || !optionValue) {
      return;
    }

    state.selections.set(optionIndex, optionValue);

    clearInvalidSelections(optionIndex);
    renderOptions();

    /* One remaining option: tapping the size adds immediately. */
    if (state.optionIndices.length === 1 && allOptionsSelected()) {
      const variant = findSelectedVariant();

      if (!variant) {
        showSheetError("Questa combinazione non è disponibile.");
        return;
      }

      addVariantToCart(variant);
    }
  }

  function handleSheetClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const closeButton = event.target.closest("[data-od-sheet-close]");

    if (closeButton) {
      event.preventDefault();

      if (!state.adding) {
        setTriggerLoading(state.trigger, false);
        closeSheet();
      }

      return;
    }

    const optionButton = event.target.closest("[data-od-option-index]");

    if (optionButton) {
      event.preventDefault();
      handleOptionSelection(optionButton);
      return;
    }

    const submitButton = event.target.closest("[data-od-sheet-submit]");

    if (submitButton) {
      event.preventDefault();

      if (!allOptionsSelected()) {
        return;
      }

      const variant = findSelectedVariant();

      if (!variant) {
        showSheetError("Questa combinazione non è disponibile.");
        return;
      }

      addVariantToCart(variant);
    }
  }

  /* ------------------------------------------------------------
   * Single delegated interaction path
   * ------------------------------------------------------------ */

  function getEventPath(event) {
    const path =
      typeof event.composedPath === "function"
        ? event.composedPath()
        : [];

    if (path.length) {
      return path;
    }

    /* Fallback for browsers without composedPath. */
    const fallback = [];

    let node = event.target;

    while (node) {
      fallback.push(node);
      node = node.parentNode;
    }

    return fallback;
  }

  function findInPath(path, selector) {
    for (const node of path) {
      if (node instanceof Element && node.matches(selector)) {
        return node;
      }
    }

    return null;
  }

  function swallow(event) {
    event.preventDefault();
    event.stopPropagation();

    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }
  }

  function handleCardClick(event) {
    /* Ignore synthetic, modified and non-primary clicks. */
    if (
      event.button > 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const path = getEventPath(event);

    /* Our own modal handles itself. */
    if (findInPath(path, ".od-quick-add")) {
      return;
    }

    const card = findInPath(path, "product-card");

    if (!card) {
      return;
    }

    const swatch = findInPath(path, SWATCH_SELECTOR);

    if (swatch) {
      swallow(event);
      activateSwatch(swatch, card);
      return;
    }

    /* Carousel arrows, dots, media tabs, "+N colours" link. */
    if (findInPath(path, IGNORE_SELECTOR)) {
      return;
    }

    const trigger = findInPath(path, QUICK_ADD_SELECTOR);

    if (!trigger) {
      return;
    }

    swallow(event);

    openQuickAdd(card, trigger);
  }

  /*
   * Registered on window, capture phase: this is the earliest point in
   * the DOM event flow, so it runs before any Aurora listener bound on
   * document, on <product-form>, on <quick-view-button> or on the
   * button itself. Because the click is swallowed here, the native
   * form submit never fires and no separate submit handler is needed.
   */
  window.addEventListener("click", handleCardClick, true);

  /* Keep the selected desktop image after the swatch row loses hover. */
  document.addEventListener(
    "mouseleave",
    (event) => {
      if (MOBILE_QUERY.matches) {
        return;
      }

      if (
        !(event.target instanceof Element) ||
        !event.target.matches("color-swatches")
      ) {
        return;
      }

      restoreDesktopSelection(
        event.target.closest("product-card"),
        180
      );
    },
    true
  );

  document.addEventListener("keydown", (event) => {
    if (
      event.key === "Escape" &&
      sheet?.classList.contains("is-open") &&
      !state.adding
    ) {
      setTriggerLoading(state.trigger, false);
      closeSheet();
    }
  });

  ensureSupportStyles();
})();